import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { solutionKeys } from "@/lib/solution-keys";

export interface CommentRow {
  id: string;
  versionId: string;
  versionNumber: number;
  elementId: string;
  elementType: string;
  authorId: string;
  authorName: string;
  authorEmail: string;
  authorImage: string | null;
  body: string;
  resolved: boolean;
  parentId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CommentBundle {
  own: CommentRow[];
  carriedOver: CommentRow[];
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) {
    let message = `${res.status}`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

/** Loads all comments for a version, plus unresolved carry-over from prior versions. */
export function useVersionComments(versionId: string | null | undefined) {
  return useQuery({
    queryKey: solutionKeys.comments(versionId ?? "none"),
    enabled: Boolean(versionId),
    queryFn: async (): Promise<CommentBundle> => {
      const json = await api<{ data: CommentBundle }>(
        `/api/versions/${versionId}/comments?carryOver=true`,
      );
      return json.data;
    },
  });
}

export function useCreateComment(versionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      elementId: string;
      elementType: string;
      body: string;
      parentId?: string | null;
    }) => {
      const json = await api<{ data: CommentRow }>(`/api/versions/${versionId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.comments(versionId) });
      qc.invalidateQueries({ queryKey: solutionKeys.all });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to add comment");
    },
  });
}

export function useUpdateComment(versionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { commentId: string; body?: string; resolved?: boolean }) => {
      const { commentId, ...rest } = input;
      const json = await api<{ data: CommentRow }>(`/api/comments/${commentId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(rest),
      });
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.comments(versionId) });
      qc.invalidateQueries({ queryKey: solutionKeys.all });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to update comment");
    },
  });
}

export function useDeleteComment(versionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (commentId: string) => {
      await api(`/api/comments/${commentId}`, { method: "DELETE" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.comments(versionId) });
      qc.invalidateQueries({ queryKey: solutionKeys.all });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to delete comment");
    },
  });
}
